import { supabase } from '../lib/supabase';

// Global Open Graph image for all match pages
const GLOBAL_OG_IMAGE_URL = 'https://your-domain.com/images/match-og-default.jpg';
type MatchProcessingStatus =
  | 'pending'
  | 'analyzing'
  | 'cancelled'
  | 'error'
  | 'completed'
  | 'deleted'
  | 'success';

// Helper function to get global OG image URL
export const getGlobalOGImageUrl = (): string => GLOBAL_OG_IMAGE_URL;

export interface MatchPublishingStatus {
  id: string;
  match_id: string;
  is_published: boolean;
  published_at: string | null;
  published_by: string | null;
  created_at: string;
  updated_at: string;
  // SEO & URL Management
  public_slug: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  custom_url: string | null;
  // Match Center Configuration
  details?: boolean;
  lineup?: boolean;
  timeline?: boolean;
  commentary?: boolean;
  stats?: boolean;
  // Client integration (optional; for client-submitted matches)
  organisation_id?: string | null;
  external_match_id?: string | null;
  processing_status?: MatchProcessingStatus | null;
  submission_payload?: Record<string, unknown> | null;
  error_message?: string | null;
}

export interface PublishMatchData {
  match_id: string;
  is_published: boolean;
  public_slug?: string;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  custom_url?: string;
}

// Helper function to generate SEO-friendly slug
export const generateMatchSlug = (homeTeam: string, awayTeam: string, date: string): string => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }).toLowerCase().replace(/\s+/g, '-');
  };

  const cleanTeamName = (team: string) => 
    team.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

  const home = cleanTeamName(homeTeam);
  const away = cleanTeamName(awayTeam);
  const formattedDate = formatDate(date);
  
  return `${home}-vs-${away}-${formattedDate}`;
};

export const matchPublishingService = {
  // Update match center configuration
  async updateMatchCenterConfig(
    matchId: string, 
    config: {
      is_published: boolean;
      details: boolean;
      lineup: boolean;
      timeline: boolean;
      commentary: boolean;
      stats: boolean;
    }
  ) {
    console.log('Updating match center config for match:', matchId, config);
    
    try {
      const { data, error } = await supabase
        .from('match_publishing')
        .upsert({
          match_id: matchId,
          is_published: config.is_published,
          details: config.details,
          lineup: config.lineup,
          timeline: config.timeline,
          commentary: config.commentary,
          stats: config.stats,
          updated_at: new Date().toISOString()
        }, { onConflict: 'match_id' })
        .select();

      if (error) {
        console.error('Error updating match center config:', error);
        throw error;
      }

      console.log('Match center config updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Service error updating match center config:', error);
      throw error;
    }
  },
  // Get publishing status for a specific match
  async getMatchPublishingStatus(matchId: string): Promise<MatchPublishingStatus | null> {
    try {
      const { data, error } = await supabase
        .from('match_publishing')
        .select('*')
        .eq('match_id', matchId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found - match is not published
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching match publishing status:', error);
      throw error;
    }
  },

  // Get publishing status for multiple matches
  async getMatchesPublishingStatus(matchIds: string[]): Promise<MatchPublishingStatus[]> {
    try {
      const { data, error } = await supabase
        .from('match_publishing')
        .select('*')
        .in('match_id', matchIds);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching matches publishing status:', error);
      throw error;
    }
  },

  // Publish or unpublish a match
  async toggleMatchPublishing(matchId: string, isPublished: boolean, matchData?: { homeTeam: string; awayTeam: string; date: string }): Promise<MatchPublishingStatus> {
    try {
      console.log(`toggleMatchPublishing called for match ${matchId}, isPublished: ${isPublished}`);
      
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw new Error(`Authentication failed: ${authError.message}`);
      }
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log(`User authenticated: ${user.id}`);

      // Check if publishing status already exists
      const existingStatus = await this.getMatchPublishingStatus(matchId);
      console.log(`Existing status for match ${matchId}:`, existingStatus);

        if (existingStatus) {
          // Update existing status
          console.log(`Updating existing publishing status for match ${matchId}`);
          
          const updateData: any = {
            is_published: isPublished,
            published_at: isPublished ? new Date().toISOString() : null,
            published_by: isPublished ? user.id : null,
            updated_at: new Date().toISOString()
          };

          // Generate SEO data if publishing and match data is provided
          if (isPublished && matchData) {
            updateData.public_slug = generateMatchSlug(matchData.homeTeam, matchData.awayTeam, matchData.date);
            updateData.seo_title = `${matchData.homeTeam} vs ${matchData.awayTeam} | Match Report`;
            updateData.seo_description = `Complete match report, stats, and highlights from ${matchData.homeTeam} vs ${matchData.awayTeam}`;
            updateData.seo_keywords = `${matchData.homeTeam}, ${matchData.awayTeam}, football, match report, stats, highlights`;
          }

          const { data, error } = await supabase
            .from('match_publishing')
            .update(updateData)
            .eq('match_id', matchId)
            .select()
            .single();

        if (error) {
          console.error('Update error:', error);
          throw new Error(`Failed to update publishing status: ${error.message} (Code: ${error.code})`);
        }

        console.log(`Successfully updated publishing status for match ${matchId}`);
        return data;
        } else {
          // Create new publishing status
          console.log(`Creating new publishing status for match ${matchId}`);
          
          const insertData: any = {
            match_id: matchId,
            is_published: isPublished,
            published_at: isPublished ? new Date().toISOString() : null,
            published_by: isPublished ? user.id : null
          };

          // Generate SEO data if publishing and match data is provided
          if (isPublished && matchData) {
            insertData.public_slug = generateMatchSlug(matchData.homeTeam, matchData.awayTeam, matchData.date);
            insertData.seo_title = `${matchData.homeTeam} vs ${matchData.awayTeam} | Match Report`;
            insertData.seo_description = `Complete match report, stats, and highlights from ${matchData.homeTeam} vs ${matchData.awayTeam}`;
            insertData.seo_keywords = `${matchData.homeTeam}, ${matchData.awayTeam}, football, match report, stats, highlights`;
          }

          const { data, error } = await supabase
            .from('match_publishing')
            .insert(insertData)
            .select()
            .single();

        if (error) {
          console.error('Insert error:', error);
          throw new Error(`Failed to create publishing status: ${error.message} (Code: ${error.code})`);
        }

        console.log(`Successfully created publishing status for match ${matchId}`);
        return data;
      }
    } catch (error) {
      console.error('Error toggling match publishing status:', error);
      throw error;
    }
  },

  // Get all published matches (for landing page API)
  async getPublishedMatches(): Promise<MatchPublishingStatus[]> {
    try {
      const { data, error } = await supabase
        .from('match_publishing')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching published matches:', error);
      throw error;
    }
  },

  // Get published match IDs (for caching)
  async getPublishedMatchIds(): Promise<string[]> {
    try {
      const publishedMatches = await this.getPublishedMatches();
      return publishedMatches.map(match => match.match_id);
    } catch (error) {
      console.error('Error fetching published match IDs:', error);
      throw error;
    }
  },

  // --- Client integration: resolve match by org + external match id (for get-stats) ---
  async getMatchByOrganisationAndExternalId(
    organisationId: string,
    externalMatchId: string
  ): Promise<MatchPublishingStatus | null> {
    try {
      const { data, error } = await supabase
        .from('match_publishing')
        .select('*')
        .eq('organisation_id', organisationId)
        .eq('external_match_id', externalMatchId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data as MatchPublishingStatus;
    } catch (error) {
      console.error('Error resolving match by org + external id:', error);
      return null;
    }
  },

  // --- Client integration: upsert match_publishing for client submission (create-match) ---
  async upsertForClientSubmission(
    matchId: string,
    organisationId: string,
    externalMatchId: string,
    processingStatus: MatchProcessingStatus = 'pending',
    submissionPayload?: Record<string, unknown> | null
  ): Promise<MatchPublishingStatus | null> {
    try {
      const row = {
        match_id: matchId,
        organisation_id: organisationId,
        external_match_id: externalMatchId,
        processing_status: processingStatus,
        submission_payload: submissionPayload ?? null,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('match_publishing')
        .upsert(row, { onConflict: 'match_id' })
        .select()
        .single();

      if (error) {
        console.error('Error upserting match_publishing for client submission:', error);
        return null;
      }
      return data as MatchPublishingStatus;
    } catch (error) {
      console.error('Error in upsertForClientSubmission:', error);
      return null;
    }
  },

  // --- Client integration: update processing status (pipeline completion) ---
  async updateProcessingStatus(
    matchId: string,
    processingStatus: MatchProcessingStatus,
    errorMessage?: string | null
  ): Promise<boolean> {
    try {
      const update: Record<string, unknown> = {
        processing_status: processingStatus,
        updated_at: new Date().toISOString(),
      };
      if (errorMessage !== undefined) update.error_message = errorMessage;

      const { error } = await supabase
        .from('match_publishing')
        .update(update)
        .eq('match_id', matchId);

      if (error) {
        console.error('Error updating processing_status:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error in updateProcessingStatus:', error);
      return false;
    }
  },

  // Publish or unpublish a match with custom SEO data
  async toggleMatchPublishingWithSEO(
    matchId: string, 
    isPublished: boolean, 
    matchData?: { homeTeam: string; awayTeam: string; date: string },
    seoData?: {
      seo_title: string;
      seo_description: string;
      seo_keywords: string;
      custom_url?: string;
    }
  ): Promise<MatchPublishingStatus> {
    try {
      console.log(`toggleMatchPublishingWithSEO called for match ${matchId}, isPublished: ${isPublished}`);
      
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw new Error(`Authentication failed: ${authError.message}`);
      }
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log(`User authenticated: ${user.id}`);

      // Check if publishing status already exists
      const existingStatus = await this.getMatchPublishingStatus(matchId);
      console.log(`Existing status for match ${matchId}:`, existingStatus);

      if (existingStatus) {
        // Update existing status
        console.log(`Updating existing publishing status for match ${matchId}`);
        
        const updateData: any = {
          is_published: isPublished,
          published_at: isPublished ? new Date().toISOString() : null,
          published_by: isPublished ? user.id : null,
          updated_at: new Date().toISOString()
        };

        // Add custom SEO data if publishing
        if (isPublished && seoData) {
          updateData.seo_title = seoData.seo_title;
          updateData.seo_description = seoData.seo_description;
          updateData.seo_keywords = seoData.seo_keywords;
          if (seoData.custom_url) {
            updateData.custom_url = seoData.custom_url;
          }
          // Generate slug if not provided
          if (matchData) {
            updateData.public_slug = seoData.custom_url || generateMatchSlug(matchData.homeTeam, matchData.awayTeam, matchData.date);
          }
        }

        const { data, error } = await supabase
          .from('match_publishing')
          .update(updateData)
          .eq('match_id', matchId)
          .select()
          .single();

        if (error) {
          console.error('Update error:', error);
          throw new Error(`Failed to update publishing status: ${error.message} (Code: ${error.code})`);
        }

        console.log(`Successfully updated publishing status for match ${matchId}`);
        return data;
      } else {
        // Create new publishing status
        console.log(`Creating new publishing status for match ${matchId}`);
        
        const insertData: any = {
          match_id: matchId,
          is_published: isPublished,
          published_at: isPublished ? new Date().toISOString() : null,
          published_by: isPublished ? user.id : null
        };

        // Add custom SEO data if publishing
        if (isPublished && seoData) {
          insertData.seo_title = seoData.seo_title;
          insertData.seo_description = seoData.seo_description;
          insertData.seo_keywords = seoData.seo_keywords;
          if (seoData.custom_url) {
            insertData.custom_url = seoData.custom_url;
          }
          // Generate slug if not provided
          if (matchData) {
            insertData.public_slug = seoData.custom_url || generateMatchSlug(matchData.homeTeam, matchData.awayTeam, matchData.date);
          }
        }

        const { data, error } = await supabase
          .from('match_publishing')
          .insert(insertData)
          .select()
          .single();

        if (error) {
          console.error('Insert error:', error);
          throw new Error(`Failed to create publishing status: ${error.message} (Code: ${error.code})`);
        }

        console.log(`Successfully created publishing status for match ${matchId}`);
        return data;
      }
    } catch (error) {
      console.error('Error toggling match publishing status with SEO:', error);
      throw error;
    }
  },

  // Update SEO data for a match (without changing publish status)
  async updateMatchSEOData(matchId: string, seoData: {
    seo_title: string;
    seo_description: string;
    seo_keywords: string;
    public_slug: string;
    custom_url?: string;
  }): Promise<MatchPublishingStatus> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('match_publishing')
        .upsert({
          match_id: matchId,
          seo_title: seoData.seo_title,
          seo_description: seoData.seo_description,
          seo_keywords: seoData.seo_keywords,
          public_slug: seoData.public_slug,
          custom_url: seoData.custom_url || null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'match_id'
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update SEO data: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating SEO data:', error);
      throw error;
    }
  },

  // Toggle only the publish status (without updating SEO data)
  async toggleMatchPublishingStatus(matchId: string, isPublished: boolean): Promise<MatchPublishingStatus> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('match_publishing')
        .upsert({
          match_id: matchId,
          is_published: isPublished,
          published_at: isPublished ? new Date().toISOString() : null,
          published_by: isPublished ? user.id : null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'match_id'
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to toggle publish status: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error toggling publish status:', error);
      throw error;
    }
  }
};
